import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Globe2, Building2, GraduationCap, Handshake, Users, Compass, Sparkles, Mic } from "lucide-react";
import { HERO_ROTATION, MARKETS_LIST, INVESTMENT_CATEGORIES, WHY_ALTURA, ARTICLES } from "../../content/site";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import MarketCard from "../../components/shared/MarketCard";
import OpportunityCard from "../../components/shared/OpportunityCard";
import ArticleCard from "../../components/shared/ArticleCard";
import LeadForm from "../../components/shared/LeadForm";
import { api } from "../../lib/api";

const WHY_ICONS = [Globe2, Handshake, GraduationCap, Users, Compass, Building2];

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setHeroIndex((i) => (i + 1) % HERO_ROTATION.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/public/opportunities?featured=true").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative -mt-24 h-[92vh] min-h-[640px] w-full overflow-hidden bg-altura-espresso" data-testid="hero-section">
        {HERO_ROTATION.map((slide, i) => (
          <div key={slide.image}
               className={`absolute inset-0 transition-opacity duration-[2000ms] ${i === heroIndex ? "opacity-100" : "opacity-0"}`}>
            <img src={slide.image} alt={slide.label}
                 className="h-full w-full object-cover animate-ken-burns" />
          </div>
        ))}
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 flex h-full items-end pt-24">
          <div className="container-editorial pb-24 sm:pb-32 text-altura-ivory">
            <Eyebrow className="text-altura-gold">Invest Beyond Borders</Eyebrow>
            <EditorialHeading size="xl" className="mt-5 text-altura-ivory max-w-4xl">
              Invest in Ghana. <span className="italic text-altura-gold-light">Explore</span> Global Opportunities.
            </EditorialHeading>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-altura-ivory/85 leading-relaxed">
              Discover carefully selected international real estate opportunities, investor education, and strategic global markets curated for investors, entrepreneurs, families, and the African diaspora.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/explore-ghana" className="btn-gold" data-testid="hero-cta-explore-ghana">
                Explore Ghana <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/opportunities" className="btn-outline-gold border-altura-ivory/70 text-altura-ivory hover:bg-altura-ivory/10" data-testid="hero-cta-markets">
                View Global Markets
              </Link>
              <button
                className="text-sm text-altura-espresso hover:text-altura-espresso-2 transition inline-flex items-center gap-2 rounded-full bg-altura-ivory/95 hover:bg-white px-4 py-2 shadow-[0_1px_0_rgba(255,255,255,0.35)_inset,0_10px_24px_-14px_rgba(0,0,0,0.35)]"
                data-testid="hero-cta-talk-auri"
                onClick={() => window.dispatchEvent(new CustomEvent("altura:talk-to-auri"))}
                aria-label="Open Voice AURI"
              >
                <Mic className="h-3.5 w-3.5 text-altura-gold-dark" />
                Talk to AURI
              </button>
              <button
                className="text-sm text-altura-ivory/85 hover:text-altura-gold transition inline-flex items-center gap-2 rounded-full border border-altura-ivory/25 px-4 py-2 hover:border-altura-gold/70 hover:bg-altura-ivory/5"
                data-testid="hero-cta-auri"
                onClick={() => window.dispatchEvent(new CustomEvent("altura:ask-auri", { detail: { prompt: "" } }))}
                aria-label="Open Ask AURI concierge"
              >
                <Sparkles className="h-3.5 w-3.5 text-altura-gold" />
                Ask AURI
              </button>
            </div>
          </div>
        </div>
        {/* slide indicators */}
        <div className="absolute bottom-8 right-8 z-10 flex items-center gap-2">
          {HERO_ROTATION.map((s, i) => (
            <button key={s.image} onClick={() => setHeroIndex(i)} aria-label={`Show ${s.label}`}
                    className={`h-1.5 rounded-full transition-all ${i === heroIndex ? "w-8 bg-altura-gold" : "w-4 bg-altura-ivory/40 hover:bg-altura-ivory/70"}`} />
          ))}
        </div>
      </section>

      {/* GHANA FLAGSHIP */}
      <section className="relative bg-altura-ivory py-28 sm:py-36 reveal-on-scroll" data-testid="ghana-flagship-section">
        <div className="container-editorial">
          <div className="grid gap-14 lg:grid-cols-12 items-center">
            <div className="lg:col-span-5">
              <Eyebrow>Flagship Market · 🇬🇭</Eyebrow>
              <EditorialHeading size="lg" className="mt-4">The heart of Altura Global is <span className="italic">Ghana</span>.</EditorialHeading>
              <GoldDivider className="mt-6" />
              <p className="mt-6 text-altura-charcoal/75 leading-relaxed">
                Ghana anchors the Altura Global thesis: a stable, English-speaking economy welcoming diaspora capital, entrepreneurship, and long-hold real estate. We know it deeply — the neighborhoods, the developers, the diligence.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-altura-charcoal/75">
                {["Residential, luxury, land, commercial, and business opportunities", "Diaspora investment specialization", "Coordinated legal, tax, and lifestyle onboarding", "Curated developer & concierge relationships"].map((li) => (
                  <li key={li} className="flex gap-3"><span className="mt-2 h-1 w-4 bg-altura-gold" /> {li}</li>
                ))}
              </ul>
              <div className="mt-9 flex items-center gap-3">
                <Link to="/explore-ghana" className="btn-gold" data-testid="ghana-cta-explore">Explore Ghana <ArrowUpRight className="h-4 w-4" /></Link>
                <Link to="/contact" className="btn-ghost-espresso" data-testid="ghana-cta-consult">Speak with an advisor</Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
                <div className="col-span-4 row-span-4 overflow-hidden rounded-lg">
                  <img src="https://images.pexels.com/photos/32803403/pexels-photo-32803403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Accra skyline" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 overflow-hidden rounded-lg">
                  <img src="https://images.pexels.com/photos/18285958/pexels-photo-18285958.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=520" alt="Luxury interior Ghana" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 overflow-hidden rounded-lg">
                  <img src="https://images.unsplash.com/photo-1730453255648-af776b331035?crop=entropy&cs=srgb&fm=jpg&q=85&w=520" alt="Ghana coastal land" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-4 row-span-2 overflow-hidden rounded-lg">
                  <img src="https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=1200" alt="Ghana residence" className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL MARKETS */}
      <section className="bg-altura-ivory-2 py-28 sm:py-36 reveal-on-scroll" data-testid="markets-section">
        <div className="container-editorial">
          <SectionHeader eyebrow="Explore Our Markets" title="Ghana leads. Six global destinations follow." subtitle="A curated portfolio of markets, evaluated for stability, transparency, and long-term merit." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[minmax(240px,auto)]">
            {MARKETS_LIST.map((m) => (
              <MarketCard key={m.slug} market={m} prominent={m.is_flagship} />
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT CATEGORIES */}
      <section className="bg-altura-ivory py-28 sm:py-36 reveal-on-scroll" data-testid="categories-section">
        <div className="container-editorial">
          <SectionHeader eyebrow="Investment Categories" title="Seven investment shapes. One disciplined approach." />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {INVESTMENT_CATEGORIES.map((c) => (
              <Link key={c.key} to={`/opportunities?type=${c.key}`} className="card-editorial group block" data-testid={`category-card-${c.key}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={c.image} alt={c.label} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-altura-espresso/70 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="font-editorial text-lg text-altura-ivory leading-tight">{c.label}</div>
                    <div className="mt-1 text-[11px] text-altura-ivory/70 line-clamp-2">{c.body}</div>
                  </div>
                </div>
              </Link>
            ))}
            {/* Fills the 8th grid slot on lg screens (7 categories → last col
                was blank). Turns dead space into a TGL affiliate CTA. */}
            <Link to="/global-link"
                  className="group aspect-square flex flex-col items-center justify-center rounded-lg border border-altura-gold/40 bg-gradient-to-br from-altura-espresso to-[#2a1610] text-altura-ivory p-6 text-center transition-all hover:border-altura-gold hover:shadow-[0_30px_60px_-20px_rgba(179,143,70,0.4)]"
                  data-testid="category-card-tgl-cta">
              <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_invest-beyond-2/artifacts/ru517vq0_G%20LOGO%20JPEG.jpg"
                   alt="The Global Link"
                   className="w-20 h-20 rounded-full object-contain bg-altura-ivory p-1.5 transition-transform duration-700 group-hover:scale-110" />
              <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-altura-gold">The Global Link</div>
              <div className="mt-2 font-editorial text-lg leading-tight">Turn your network into income</div>
              <div className="mt-3 text-[11px] text-altura-ivory/70">Earn $250 per referred sale · Join TGL →</div>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED OPPORTUNITIES */}
      {featured.length > 0 && (
        <section className="bg-altura-ivory-2 py-28 sm:py-36 reveal-on-scroll" data-testid="featured-opportunities-section">
          <div className="container-editorial">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <SectionHeader eyebrow="Featured Opportunities" title="A restrained selection, updated regularly." size="md" />
              <Link to="/opportunities" className="link-underline text-sm text-altura-espresso" data-testid="cta-view-all-opportunities">View all →</Link>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((o) => <OpportunityCard key={o.id} opportunity={o} />)}
            </div>
          </div>
        </section>
      )}

      {/* WHY ALTURA */}
      <section className="bg-altura-ivory-2 py-28 sm:py-36 reveal-on-scroll" data-testid="why-altura-section">
        <div className="container-editorial relative z-10">
          <SectionHeader eyebrow="Why Altura Global" title="A different kind of international investment firm." subtitle="Education-first, relationship-focused, and Ghana-led. We measure our work in trust, not transactions." />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {WHY_ALTURA.map((w, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div key={w.title} data-testid={`why-card-${i}`}>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-altura-gold/60 bg-white text-altura-gold-dark shadow-[0_2px_8px_rgba(179,143,70,0.15)]">
                    <Icon className="h-4 w-4" />
                  </div>
                  <h3 className="mt-6 font-editorial text-2xl text-altura-charcoal leading-snug">{w.title}</h3>
                  <p className="mt-3 text-sm text-altura-charcoal/70 leading-relaxed">{w.body}</p>
                  <div className="mt-4 h-px w-10 bg-altura-burgundy/40" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEARNING CENTER */}
      <section className="bg-altura-ivory py-28 sm:py-36 reveal-on-scroll" data-testid="learning-preview-section">
        <div className="container-editorial">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeader eyebrow="Learning Center" title="Editorial notes for the global investor." subtitle="Written for people building wealth across borders. No hype, no shortcuts." size="md" />
            <Link to="/learning-center" className="link-underline text-sm text-altura-espresso" data-testid="cta-view-all-articles">All essays →</Link>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 3).map((a) => <ArticleCard key={a.slug} article={a} />)}
          </div>
        </div>
      </section>

      {/* INVESTOR GUIDE */}
      <section className="bg-altura-ivory-2 py-28 sm:py-36 reveal-on-scroll" data-testid="guide-section">
        <div className="container-editorial grid gap-14 lg:grid-cols-2 items-center">
          <div>
            <Eyebrow className="text-altura-gold-dark">Complimentary</Eyebrow>
            <EditorialHeading size="lg" className="mt-4">The Global Investment Starter Guide.</EditorialHeading>
            <p className="mt-6 text-altura-charcoal/70 max-w-lg leading-relaxed">
              A foundational primer for investors considering their first international real-estate opportunity. Ghana-led, globally minded.
            </p>
            <GoldDivider className="mt-8" />
            <ul className="mt-6 space-y-3 text-sm text-altura-charcoal/75">
              <li className="flex gap-3"><span className="mt-2 h-1 w-4 bg-altura-gold" /> Framework for evaluating international markets</li>
              <li className="flex gap-3"><span className="mt-2 h-1 w-4 bg-altura-gold" /> What to understand before your first consultation</li>
              <li className="flex gap-3"><span className="mt-2 h-1 w-4 bg-altura-gold" /> Ghana-specific opportunity landscape</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-altura-gray-line bg-white p-8 shadow-[0_1px_2px_rgba(20,20,25,0.04)]">
            <LeadForm variant="guide" sourcePage="/#guide" testIdPrefix="guide-form" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-altura-ivory py-28 sm:py-36 reveal-on-scroll" data-testid="final-cta-section">
        <div className="container-editorial text-center">
          <Eyebrow className="mx-auto">Begin the conversation</Eyebrow>
          <EditorialHeading size="lg" className="mt-4 mx-auto max-w-3xl">Ready to <span className="italic">Invest Beyond Borders?</span></EditorialHeading>
          <p className="mt-5 mx-auto max-w-2xl text-altura-charcoal/70">
            Whether you are exploring Ghana for the first time or evaluating your next global asset, our team is here — patient, considered, and quietly excellent.
          </p>
          <GoldDivider className="mt-6 mx-auto" />
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="btn-gold" data-testid="final-cta-request">Request Information</Link>
            <Link to="/consultation" className="btn-espresso" data-testid="final-cta-consultation">Schedule a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
